import React from 'react';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';

export interface TopItem {
  /** Optional unique key (falls back to label) */
  id?: string;
  /** Full display label — wrapping allowed */
  label: string;
  /** Numeric value shown right-aligned */
  value: number;
  /** Drill-through URL (internal route). Optional when `onActivate` is used. */
  href?: string;
  /** Invoked instead of navigating when the row is clicked */
  onActivate?: () => void;
}

export interface TopItemsListProps {
  items: TopItem[];
  /** Custom value formatter (default: plain number) */
  valueFormat?: (v: number) => string;
  /** Column header for the value (default: "Value") */
  valueLabel?: string;
  className?: string;
}

const defaultFormat = (v: number): string => String(v);

/**
 * Compact ranked list showing full labels with values and drill-through.
 * Designed to sit below a horizontal bar chart where axis labels are truncated.
 * Shows all items — no artificial cap.
 */
export const TopItemsList: React.FC<TopItemsListProps> = ({
  items,
  valueFormat = defaultFormat,
  valueLabel = 'Value',
  className,
}) => {
  const navigate = useNavigate();

  if (items.length === 0) return null;

  return (
    <div className={cn('mt-3 border-t border-border pt-3', className)}>
      {/* Header */}
      <div className="flex items-center gap-2 px-1 pb-1.5 text-[10px] uppercase tracking-wider text-muted-foreground">
        <span className="w-5 text-right">#</span>
        <span className="flex-1">Name</span>
        <span className="w-16 text-right">{valueLabel}</span>
      </div>
      {/* Rows */}
      <div className="space-y-0">
        {items.map((item, idx) => (
          <button
            key={item.id ?? item.label}
            type="button"
            onClick={() => {
              if (item.onActivate) item.onActivate();
              else navigate(item.href ?? '/app/activities');
            }}
            className="w-full flex items-start gap-2 px-1 py-1.5 text-xs rounded transition-colors hover:bg-muted/50 text-left group"
          >
            <span className="w-5 text-right text-muted-foreground tabular-nums shrink-0">
              {idx + 1}
            </span>
            <span className="flex-1 text-foreground group-hover:text-primary break-words leading-snug">
              {item.label}
            </span>
            <span className="w-16 text-right text-muted-foreground tabular-nums shrink-0">
              {valueFormat(item.value)}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
};
